// FTEDN Creator — Before/After Journal Panel
import { useEffect, useState } from 'react';
import { apiClient } from '../../hooks/useApi';
import { formatJournalDay, type JournalEntry } from '../../stores/product.store';
import { validateFeedback } from '../BetaProgram/beta.helpers';
import { useToast } from '../../components/ui/Toast';

interface JournalPanelProps {
  productId: string;
  productName: string;
  programId: string;
  startDate?: string;
}

interface JournalFeedback {
  id?: string;
  programId?: string;
  productId?: string;
  type?: string;
  content?: string;
  createdAt?: string;
}

function toJournalEntry(feedback: JournalFeedback, startDate: string): JournalEntry {
  const createdAt = typeof feedback.createdAt === 'string' ? feedback.createdAt : new Date().toISOString();
  const date = createdAt.split('T')[0] || createdAt;

  return {
    id: feedback.id ?? `journal-${createdAt}-${feedback.content ?? ''}`,
    date,
    notes: feedback.content ?? '',
    day: formatJournalDay(startDate, date),
  };
}

function isProductJournalEntry(feedback: JournalFeedback, productId: string, programId: string) {
  if (feedback.type !== 'notes') return false;
  if (feedback.programId !== programId) return false;
  if (feedback.productId && feedback.productId !== productId) return false;
  return true;
}

export function JournalPanel({
  productId,
  productName,
  programId,
  startDate = new Date().toISOString(),
}: JournalPanelProps) {
  const { addToast } = useToast();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadEntries() {
      if (!programId) {
        setEntries([]);
        return;
      }

      try {
        const res = await apiClient.get(`/feedback/my/${encodeURIComponent(programId)}`);
        const feedback = Array.isArray(res.data) ? res.data as JournalFeedback[] : [];
        const nextEntries = feedback
          .filter(entry => isProductJournalEntry(entry, productId, programId))
          .sort((a, b) => Date.parse(a.createdAt ?? '') - Date.parse(b.createdAt ?? ''))
          .map(entry => toJournalEntry(entry, startDate));
        if (!cancelled) setEntries(nextEntries);
      } catch {
        if (!cancelled) setEntries([]);
      }
    }

    void loadEntries();

    return () => {
      cancelled = true;
    };
  }, [productId, programId, startDate]);

  const handleAdd = async () => {
    const content = notes.trim();
    const validation = validateFeedback({ type: 'notes', content, programId });
    if (!validation.valid) {
      addToast({ type: 'warning', title: 'Missing info', message: validation.error });
      return;
    }

    setSubmitting(true);
    try {
      const createdAt = new Date().toISOString();
      const res = await apiClient.post('/feedback', {
        type: 'notes',
        content,
        programId,
        productId,
      });
      if (res.success) {
        setEntries(prev => [
          ...prev,
          toJournalEntry({
            id: res.data?.id,
            type: 'notes',
            content,
            programId,
            productId,
            createdAt,
          }, startDate),
        ]);
        addToast({ type: 'xp', title: '+10 XP', message: 'Journal entry added!' });
        setNotes('');
      } else {
        addToast({ type: 'error', title: 'Error', message: 'Submission failed' });
      }
    } catch {
      addToast({ type: 'error', title: 'Error', message: 'Could not reach server' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="journal-panel">
      <h3>📔 Before/After Journal</h3>
      <p className="journal-desc">Track your progress daily. Compare how the product affects you over time.</p>

      {/* Existing entries */}
      {entries.length > 0 && (
        <div className="journal-entries">
          {entries.map(entry => (
            <div key={entry.id} className="journal-entry">
              <div className="journal-day-badge">Day {entry.day}</div>
              <div className="journal-entry-body">
                <p>{entry.notes}</p>
                <span className="journal-date">{entry.date}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New entry form */}
      <div className="journal-form">
        <textarea
          className="neo-input journal-textarea"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder={`How is ${productName} working today?`}
          rows={3}
        />
        <button className="neo-btn neo-btn--primary journal-submit" onClick={handleAdd} disabled={submitting}>
          {submitting ? 'Adding...' : '📝 Add Entry'}
        </button>
      </div>
    </div>
  );
}
